import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { breakpoint } from '../layout/styles'

interface CustomSelectorProps {
  selectText: string
  items: {
    id: string;
    dataTarget: string;
    linkTitle: string;
    matchTitle: string;
  }[]
  onItemSelected?: (id: string) => void
}

/**
 * CustomSelector Component
 * 
 * A dropdown-like component that allows users to select from a list of items.
 * It shows the selected item as the active label.
 * 
 * Props:
 * - selectText: Default text displayed when no item is selected.
 * - items: Array of objects containing details for each selectable item.
 * - onItemSelected: callback function for the selected option
 */
const CustomSelector = (props: CustomSelectorProps) => {
  // State for dropdown open/close
  const [isOpen, setIsOpen] = useState(false)

  // State for the selected text which is displayed
  const [selectedText, setSelectedText] = useState(props.selectText)

  const wrapperRef = useRef(null)

  // Close the dropdown if clicked outside
  const handleClickOutside = (event: MouseEvent) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [])

  return (
    <Selector isOpen={isOpen} ref={wrapperRef}>
      {/* Selected item display which also acts as a button to toggle dropdown */}
      <SelectedContent onClick={() => setIsOpen(!isOpen)}>
        {selectedText}
      </SelectedContent>

      {/* Dropdown list of items */}
      <SelectorItems>
        {props.items.map((item) => (
          <SelectorItem
            key={item.id}
            onClick={() => {
              // Close dropdown and update selected text when an item is clicked
              setIsOpen(false)
              setSelectedText(item.linkTitle)

              // Call the onItemSelected function if provided
              props.onItemSelected && props.onItemSelected(item.id)
            }}
          >
            {item.linkTitle}
          </SelectorItem>
        ))}
      </SelectorItems>
    </Selector>
  )
}

export default CustomSelector

const SelectorItems = styled.div<any>`
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1;
  width: 100%;
  max-height: 150px;
  overflow-y: auto;
`

export const Selector = styled.div<any>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  text-align: center;
  height: 40px;
  border-radius: 4px;
  box-shadow: 0 0 0 1px var(--black) inset;
  background: url(/images/arrow-down.svg) calc(100% - 10px) center no-repeat;
  background-size: 16px;
  width: 100%;

  @media ${breakpoint.desktop} {
    width: auto;
  }

  &:hover,
  &:focus {
    box-shadow: 0 0 0 1px var(--black) inset;
  }

  ${({ isOpen }) => isOpen && css`
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    
    ${SelectorItems} {
      display: flex;
      flex-direction: column;
      background: var(--background);
      box-shadow: 0 0 0 1px var(--black) inset;
    }
  `}
`

const SelectedContent = styled.div<any>``

const SelectorItem = styled.span<any>`
  padding: 8px 10px;

  &:not(:last-child) {
    border-bottom: 1px solid;
  }
`
